import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useS3Filter } from "../../contexts/S3FilterContext";
import PartnerFilterComponent from "./PartnerFilterComponent";
import YearFilterComponent from "./YearFilterComponent";
import MonthFilterComponent from "./MonthFilterComponent";
import SearchTextFilterComponent from "./SearchTextFilterComponent";
import FilterActionControls from "./FilterActionControls";
import { themeColors } from "../../utils/theme";

/**
 * Props for S3FilterPanel
 */
export interface S3FilterPanelProps {
  onFiltersApplied?: () => void;
  disabled?: boolean;
  loading?: boolean;
  basePath?: string;
  variant?: "standalone" | "integrated";
}

/**
 * S3FilterPanel - Main filter UI container for S3 Browser
 *
 * Composes all filter components (partner, year, month) with action controls.
 * Provides responsive layout using MUI Grid system.
 * Includes localStorage persistence for filter state.
 */
export const S3FilterPanel: React.FC<S3FilterPanelProps> = ({
  onFiltersApplied,
  disabled = false,
  loading = false,
  basePath = "",
  variant = "standalone",
}) => {
  const { filters } = useS3Filter();

  // Content without Paper wrapper (for integrated mode)
  const filterContent = (
    <>
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: themeColors.textSecondary, mt: 0.5 }}
        >
          Refine your search by selecting partners, year, and months
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Partner Filter */}
        <Grid item xs={12} sm={6} md={3}>
          <PartnerFilterComponent
            disabled={disabled}
            loading={loading}
            basePath={basePath}
          />
        </Grid>

        {/* Year Filter */}
        <Grid item xs={12} sm={6} md={3}>
          <YearFilterComponent
            disabled={disabled}
            loading={loading}
            basePath={basePath}
          />
        </Grid>

        {/* Month Filter */}
        <Grid item xs={12} sm={6} md={3}>
          <MonthFilterComponent
            disabled={disabled}
            loading={loading}
            yearFilter={filters.selectedYear}
            basePath={basePath}
          />
        </Grid>

        {/* Search Text Filter */}
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextFilterComponent disabled={disabled} loading={loading} />
        </Grid>
      </Grid>

      {/* Filter Action Controls */}
      {/*       <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <FilterActionControls
          onApply={onFiltersApplied}
          disabled={disabled}
          loading={loading}
        />
      </Box> */}
    </>
  );

  // Standalone variant: wrap in Paper
  if (variant === "standalone") {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          bgcolor: themeColors.paper,
          borderRadius: 2,
        }}
      >
        {filterContent}
      </Paper>
    );
  }

  // Integrated variant: just return the content
  return <Box sx={{ p: 3, pb: 2 }}>{filterContent}</Box>;
};

export default S3FilterPanel;
